import { useContext, useEffect, useMemo, useState } from 'react';

import Message from '../../../components/ui/Message';
import FormModal from '../../../components/ui/FormModal';
import { LoyaltyContext } from '../../../context/loyalty/LoyaltyContext';
import { Reward } from '../../../models/interfaces';

export const LoyaltyRewardsTab = () => {
    const { rewards, loadRewards, addReward, updateReward, deleteReward } = useContext(LoyaltyContext);

    const [isLoading, setIsLoading] = useState(false);
    const [buscar, setBuscar] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [editing, setEditing] = useState<Reward | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [form, setForm] = useState({ recompensa: '', requiere: '' });
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const [toDelete, setToDelete] = useState<Reward | null>(null);

    const refresh = async () => {
        setIsLoading(true);
        try {
            await loadRewards(0);
        } catch (e) {
            setError(e?.response?.data?.error || 'No se pudieron cargar las recompensas, intente de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    // Ordenadas por lo que requieren, para ver la escala de beneficios
    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        return [...rewards]
            .filter(r => !term || String(r.recompensa ?? '').toLowerCase().includes(term))
            .sort((a, b) => Number(a.requiere) - Number(b.requiere));
    }, [rewards, buscar]);

    const openForm = (reward: Reward | null) => {
        setIsNew(!reward);
        setEditing(reward ?? ({ id: 0, recompensa: '', requiere: 0, idEmpresa: 0 } as Reward));
        setForm({ recompensa: reward?.recompensa ?? '', requiere: reward ? String(reward.requiere) : '' });
        setSubmitted(false);
        setFormError('');
    };

    const formErrors = {
        recompensa: !form.recompensa.trim(),
        requiere: !(Number(form.requiere) > 0) || !Number.isInteger(Number(form.requiere)),
    };
    const invalid = (field: keyof typeof formErrors) => submitted && formErrors[field] ? 'is-invalid' : '';

    const handleSave = async () => {
        setSubmitted(true);
        setFormError('');
        if (Object.values(formErrors).some(Boolean) || !editing) return;

        setSaving(true);
        const payload = { ...editing, recompensa: form.recompensa.trim(), requiere: Number(form.requiere) };
        const resp = isNew ? await addReward(payload) : await updateReward(payload);
        setSaving(false);

        if (resp !== 'success') {
            setFormError(resp);
            return;
        }
        setEditing(null);
        setSuccess(isNew ? 'Recompensa creada correctamente.' : 'Recompensa actualizada correctamente.');
        refresh();
    };

    const processDelete = async () => {
        const reward = toDelete;
        setToDelete(null);
        if (!reward) return;
        const resp = await deleteReward(reward.id);
        if (resp !== 'success') setError(resp);
        else setSuccess('Recompensa eliminada.');
    };

    return (
        <>
            <div className="row g-2 mb-3 align-items-center">
                <div className="col-md-6">
                    <div className="input-group">
                        <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                        <input
                            className="form-control"
                            placeholder="Buscar recompensa..."
                            value={buscar}
                            onChange={(e) => setBuscar(e.target.value)}
                        />
                        {buscar && (
                            <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                <i className="bi bi-x-lg" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="col-md-6 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" title="Actualizar" onClick={refresh} disabled={isLoading}>
                        <i className="bi bi-arrow-clockwise" />
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => openForm(null)}>
                        <i className="bi bi-plus-lg me-1" /> Nueva recompensa
                    </button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr className="text-muted small text-uppercase">
                            <th>Recompensa</th>
                            <th className="text-end">Requiere</th>
                            <th style={{ textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={3} className="text-center py-4">
                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                            </td></tr>
                        ) : filteredData.length > 0 ? (
                            filteredData.map(reward => (
                                <tr key={reward.id}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={styles.iconCircle}><i className="bi bi-gift" /></div>
                                            <span className="fw-medium">{reward.recompensa}</span>
                                        </div>
                                    </td>
                                    <td className="text-end fw-semibold" style={{ whiteSpace: 'nowrap' }}>
                                        {Number(reward.requiere).toLocaleString('en-US')} pts
                                    </td>
                                    <td style={{ textAlign: 'center', width: 110, whiteSpace: 'nowrap' }}>
                                        <button type="button" className="btn btn-sm btn-primary" title="Editar" onClick={() => openForm(reward)}>
                                            <i className="bi bi-pencil" />
                                        </button>
                                        <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar" onClick={() => setToDelete(reward)}>
                                            <i className="bi bi-trash" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={3} className="text-center py-4 text-muted">
                                {buscar ? 'No se encontraron recompensas para la búsqueda.' : 'No hay recompensas configuradas.'}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <FormModal
                    title={isNew ? 'Nueva recompensa' : 'Editar recompensa'}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                    saving={saving}
                    error={formError}
                >
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label small text-muted mb-1">Recompensa *</label>
                            <input className={`form-control ${invalid('recompensa')}`} value={form.recompensa} autoFocus placeholder="Ej. Café gratis"
                                onChange={(e) => setForm(prev => ({ ...prev, recompensa: e.target.value }))} />
                            <div className="invalid-feedback">La descripción es requerida.</div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small text-muted mb-1">Puntos requeridos *</label>
                            <input type="number" min={1} step={1} className={`form-control ${invalid('requiere')}`} value={form.requiere}
                                onChange={(e) => setForm(prev => ({ ...prev, requiere: e.target.value }))} />
                            <div className="invalid-feedback">Ingrese un número entero mayor a 0.</div>
                        </div>
                    </div>
                </FormModal>
            )}

            {toDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro que desea eliminar la recompensa "${toDelete.recompensa}"?`}
                    closeDialog={() => setToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {success.length > 0 && (
                <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </>
    );
};

const styles = {
    iconCircle: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fbeaf2',
        color: '#d63384',
        flexShrink: 0,
    },
};
